import { Link } from "react-router";
import { LearnerMaterialRow } from "@/components/session/SessionMaterialRow";
import { Button } from "@/components/ui/button";
import { SessionReadFailure } from "./SessionReadFailure";
import type { LearnerSessionMaterial, SessionWorkspace } from "@/lib/api/sessionPortal";
import { cancelledLine, learnerFoot, learnerFootFromServer, neighbourWords } from "@/lib/session-learner";
import type { FootSession } from "@/lib/session-learner";

const WRAPPING_LINK = "block min-h-11 content-center text-primary underline-offset-4 hover:underline";

function learnerReadings(workspace: SessionWorkspace): LearnerSessionMaterial[] {
  return (workspace.plan.unit?.materials ?? []).filter((item): item is LearnerSessionMaterial => !("placement_id" in item));
}

/** The plan as published: topic, objective, Before the session. */
export function PublishedPlan({ workspace }: { workspace: SessionWorkspace }) {
  const unit = workspace.plan.unit;
  if (!unit) return (
    <div className="grid gap-0.5">
      <p className="text-ui text-foreground">Nothing is posted for this session yet.</p>
      <p className="text-meta text-muted-foreground">Your instructor's plan appears here once it is published.</p>
    </div>
  );
  const [lead, ...objectives] = unit.learning_objectives;
  return (
    <div className="grid gap-3">
      <h3 className="text-body font-semibold text-foreground">{unit.topic}</h3>
      {lead && (
        <div className="grid gap-1">
          <h4 className="text-meta font-semibold text-muted-foreground">Session objective</h4>
          <p className="text-body text-foreground">{lead}</p>
          {objectives.length > 0 && <ul className="grid list-disc gap-0.5 ps-5 text-ui text-muted-foreground">{objectives.map((objective, index) => <li key={index}>{objective}</li>)}</ul>}
        </div>
      )}
      {unit.preparation_guidance && (
        <div className="grid gap-1">
          <h4 className="text-meta font-semibold text-muted-foreground">Before the session</h4>
          <p dir="auto" className="whitespace-pre-wrap text-body text-foreground">{unit.preparation_guidance}</p>
        </div>
      )}
    </div>
  );
}

export function Readings({ workspace, onRetry }: { workspace: SessionWorkspace; onRetry: () => void }) {
  if (workspace.plan.coursework_state === "unavailable") {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-x-3 text-ui text-muted-foreground">
        This session's reading could not be read.
        <Button type="button" variant="subtle" size="sm" onClick={onRetry}>Retry</Button>
      </div>
    );
  }
  const readings = learnerReadings(workspace);
  if (readings.length === 0) return null;
  return (
    <section aria-label="Reading for this session" className="bento-part-bleed border-t border-rule">
      <ul className="min-w-0">{readings.map((material) => <LearnerMaterialRow key={material.id} material={material} />)}</ul>
    </section>
  );
}

/** The teacher's note on a change: quoted, once per page. */
export function InstructorNote({ note }: { note: string }) {
  return (
    <figure className="grid gap-1">
      <figcaption className="text-meta font-semibold text-muted-foreground">Your instructor's note</figcaption>
      <blockquote dir="auto" className="whitespace-pre-wrap border-s-2 border-rule ps-3 text-ui text-foreground [overflow-wrap:anywhere]">{note}</blockquote>
    </figure>
  );
}

function NextScheduled({ item, firstLine, onSelectSession }: { item: FootSession; firstLine?: string | null; onSelectSession?: (id: string) => void }) {
  const words = `Next scheduled: ${neighbourWords(item)}${firstLine ? ` · ${firstLine}` : ""}`;
  return onSelectSession
    ? <Button variant="subtle" className="h-auto min-h-11 max-w-full justify-start whitespace-normal text-start" onClick={() => onSelectSession(item.id)}>{words}</Button>
    : <Link className={WRAPPING_LINK} to={item.destination.href}>{words}</Link>;
}

/** The foot: the session that met, a cancelled one between as one line, and Next scheduled. */
export function LearnerFoot({ workspace, onSelectSession, continuationRef, cancelledInFoot = true, onRetry }: {
  workspace: SessionWorkspace; onSelectSession?: (id: string) => void; continuationRef: React.Ref<HTMLElement>;
  /** A cancelled session shows once per page: after Follow up when it has a box, else here. */
  cancelledInFoot?: boolean;
  onRetry?: () => void;
}) {
  // The server's foot reads forward from its own clock; the page's sequence answers when it did not come.
  const read = workspace.continuation?.foot;
  const foot = read ? learnerFootFromServer(read) : learnerFoot(workspace);
  const cancelled = (read ? cancelledInFoot : true) ? foot?.cancelled ?? [] : [];
  const open = (item: FootSession, words: string) => onSelectSession
    ? <Button variant="subtle" className="h-auto min-h-11 max-w-full justify-start whitespace-normal text-start" onClick={() => onSelectSession(item.id)}>{words}</Button>
    : <Link className={WRAPPING_LINK} to={item.destination.href}>{words}</Link>;
  return (
    <section data-session-continuation aria-label="Session continuation" className="border-t border-rule py-3">
      <nav ref={continuationRef} aria-label="Session navigation" className="grid gap-x-6 text-ui">
        {!foot ? <SessionReadFailure part="previous and next session" retry={onRetry} /> : (
          <>
            {read?.previous.state === "unavailable" ? <SessionReadFailure part="previous session" retry={onRetry} /> : foot.previous
              ? open(foot.previous, `Previous: ${neighbourWords(foot.previous)}`)
              : <p className="inline-flex min-h-11 items-center text-meta text-muted-foreground">This is the first session.</p>}
            {cancelled.map((item) => (
              <p key={item.id} className="inline-flex min-h-11 items-center text-meta text-muted-foreground">{cancelledLine(item)}</p>
            ))}
            {cancelledInFoot && read?.cancelled.state === "unavailable" && <SessionReadFailure part="cancelled sessions" retry={onRetry} />}
            {read?.next_scheduled.state === "unavailable" ? <SessionReadFailure part="next scheduled session" retry={onRetry} /> : foot.next
              ? <NextScheduled item={foot.next} firstLine={foot.next.firstLine} onSelectSession={onSelectSession} />
              : <p className="inline-flex min-h-11 items-center text-meta text-muted-foreground">No further session is scheduled.</p>}
          </>
        )}
      </nav>
    </section>
  );
}
